import path from 'node:path'
import { isEventStream, operationFileEntry, resolveDependencyOperationFile } from '@internals/shared'
import { ast, defineGenerator } from 'kubb/kit'
import type { Generator } from 'kubb/kit'
import { pluginZodName } from '@kubb/plugin-zod'
import { File, jsxRenderer } from 'kubb/jsx'
import { buildZodErrorParse, resolveRequestValidator, resolveResponseValidator } from '../builders/validatorOptions.ts'
import { getOperationSecurity, type SecurityDocument } from '../builders/security.ts'
import { Operation } from '../components/Operation.tsx'
import { MISSING_OPERATION_TYPES_WARNING, resolveOperationTypes } from '../resolveOperationTypes.ts'
import type { ContractClientFactory } from '../types.ts'

/**
 * Builds the built-in per-operation generator shared by the client plugins (`@kubb/plugin-fetch`,
 * `@kubb/plugin-axios`). Emits one async function per OpenAPI operation using the shared
 * `Operation` component: a grouped `<Name>Request` type and a function that forwards a single
 * `options` object to the bundled `client` and returns the `Unwrappable<RequestResult>`. Only the
 * generator `name` differs between plugins. Every other resolution, import, and rendering step is
 * identical.
 */
export function createClientGenerator<TFactory extends ContractClientFactory>(name: string): Generator<TFactory> {
  return defineGenerator<TFactory>({
    name,
    renderer: jsxRenderer,
    operation(node, ctx) {
      if (!ast.isHttpOperationNode(node)) return null

      const { config, driver, resolver, root } = ctx
      const { output, validator, returnType, group } = ctx.options

      const types = resolveOperationTypes(driver)
      if (!types) {
        ctx.warn(MISSING_OPERATION_TYPES_WARNING)
        return null
      }

      const validatorEnabled = resolveResponseValidator(validator) === 'zod' || resolveRequestValidator(validator) === 'zod'
      const pluginZod = validatorEnabled ? driver.getPlugin(pluginZodName) : null
      const zodResolver = pluginZod ? driver.getResolver(pluginZodName) : null

      const hasRequestBody = Boolean(node.requestBody?.content?.[0]?.schema)
      const importedTypeNames = [types.response.options(node), types.response.responses(node)]

      const importedZodNames = zodResolver
        ? [
            resolveResponseValidator(validator) === 'zod' ? zodResolver.response.response?.(node) : null,
            resolveResponseValidator(validator) === 'zod' ? (buildZodErrorParse(node, zodResolver)?.expression ?? null) : null,
            resolveRequestValidator(validator) === 'zod' && hasRequestBody ? zodResolver.response.body?.(node) : null,
          ].filter((name): name is string => Boolean(name))
        : []

      const meta = {
        name: resolver.name(node.operationId),
        file: resolver.file({ ...operationFileEntry(node, node.operationId), root, output, group: group ?? undefined }),
        fileTypes: resolveDependencyOperationFile({
          cache: ctx.cache,
          node,
          resolver: types.resolver,
          root,
          output: types.output ?? output,
          group: types.group,
        }),
        fileZod:
          zodResolver && pluginZod?.options
            ? zodResolver.file({
                ...operationFileEntry(node, node.operationId),
                root,
                output: pluginZod.options.output ?? output,
                group: pluginZod.options?.group ?? undefined,
              })
            : null,
      } as const

      const security = getOperationSecurity({
        document: ctx.adapter.document as SecurityDocument | null | undefined,
        method: node.method,
        path: node.path,
      })

      const clientPath = path.resolve(root, '.kubb/client.ts')
      const eventStream = isEventStream(node)

      return (
        <File
          baseName={meta.file.baseName}
          path={meta.file.path}
          meta={meta.file.meta}
          banner={resolver.default.banner(ctx.meta, { output, config, file: { path: meta.file.path, baseName: meta.file.baseName } })}
          footer={resolver.default.footer(ctx.meta, { output, config, file: { path: meta.file.path, baseName: meta.file.baseName } })}
        >
          <File.Import
            name={eventStream ? ['client', 'toEventStream'] : ['client', returnType === 'data' ? 'unwrapResult' : 'withUnwrap']}
            root={meta.file.path}
            path={clientPath}
          />
          <File.Import
            name={
              eventStream
                ? ['Options', 'EventStreamResult', 'SuccessOf']
                : returnType === 'data'
                  ? ['Options', 'UnwrappedResult']
                  : ['Options', 'Unwrappable', 'RequestResult']
            }
            root={meta.file.path}
            path={clientPath}
            isTypeOnly
          />

          {meta.fileTypes && importedTypeNames.length > 0 && (
            <File.Import name={Array.from(new Set(importedTypeNames))} root={meta.file.path} path={meta.fileTypes.path} isTypeOnly />
          )}

          {meta.fileZod && importedZodNames.length > 0 && <File.Import name={importedZodNames} root={meta.file.path} path={meta.fileZod.path} />}

          <Operation name={meta.name} node={node} types={types} zodResolver={zodResolver} validator={validator} returnType={returnType} security={security} />
        </File>
      )
    },
  })
}
